import { z } from 'zod';
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_TIME_UTC = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?Z$/;
function isRealCalendarDate(year, month, day) {
    const probe = new Date(Date.UTC(year, month - 1, day));
    return (probe.getUTCFullYear() === year &&
        probe.getUTCMonth() === month - 1 &&
        probe.getUTCDate() === day);
}
function isRealClockTime(hour, minute, second) {
    return hour <= 23 && minute <= 59 && second <= 59;
}
/**
 * Parses a date-only (`YYYY-MM-DD`) or UTC-timestamp (`YYYY-MM-DDTHH:mm:ss[.SSS]Z`) string as
 * UTC, or passes through a valid `Date` instance. Deliberately stricter than `new Date(string)`:
 * an ISO string without a trailing `Z` is parsed by the spec as *local time*, so a caller in one
 * timezone and a retry from another can send the same logical date and get two different
 * timestamps -- rejecting anything outside these two formats removes that ambiguity rather than
 * silently accepting it.
 */
export function parseStrictDate(value, context, invalidMessage = 'Invalid date.') {
    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) {
            context.addIssue({ code: 'custom', message: invalidMessage });
            return z.NEVER;
        }
        return value;
    }
    const dateOnly = DATE_ONLY.exec(value);
    if (dateOnly) {
        const year = Number(dateOnly[1]);
        const month = Number(dateOnly[2]);
        const day = Number(dateOnly[3]);
        if (!isRealCalendarDate(year, month, day)) {
            context.addIssue({ code: 'custom', message: invalidMessage });
            return z.NEVER;
        }
        return new Date(Date.UTC(year, month - 1, day));
    }
    const dateTime = DATE_TIME_UTC.exec(value);
    if (dateTime) {
        const year = Number(dateTime[1]);
        const month = Number(dateTime[2]);
        const day = Number(dateTime[3]);
        const hour = Number(dateTime[4]);
        const minute = Number(dateTime[5]);
        const second = Number(dateTime[6]);
        const millisecond = Number((dateTime[7] ?? '0').padEnd(3, '0'));
        if (!isRealCalendarDate(year, month, day) || !isRealClockTime(hour, minute, second)) {
            context.addIssue({ code: 'custom', message: invalidMessage });
            return z.NEVER;
        }
        return new Date(Date.UTC(year, month - 1, day, hour, minute, second, millisecond));
    }
    context.addIssue({ code: 'custom', message: invalidMessage });
    return z.NEVER;
}
//# sourceMappingURL=strict-date.js.map